import React from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { ClientDeviceStats } from '../../services/statusService';
import { groupClientStats } from './clientGroups';

/*
 * The biggest clients as horizontal stacked bars: the devices that are
 * up to date and the ones waiting for a refresh. One scale for every
 * row (the largest client fills the width), a legend from the two
 * series, a tooltip per bar. HTML bars, no chart library.
 */

interface Props {
  clients: ClientDeviceStats[];
  /** How many clients to show, the biggest first. */
  top?: number;
  /** A click on a bar: the dashboard narrows its table to that client. */
  onSelect?: (client: ClientDeviceStats) => void;
  selectedId?: string | null;
}

export const ClientsBarChart: React.FC<Props> = ({ clients, top = 8, onSelect, selectedId = null }) => {
  const { t, formatNumber } = useI18n();
  // A client with agencies is one bar, the group's fleet (docs/agences.md).
  const groups = groupClientStats(clients);
  const agencyCount = new Map(groups.map((g) => [g.root.client_id, g.agencies.length]));
  const rows = groups.map((g) => g.total).sort((a, b) => b.total_devices - a.total_devices).slice(0, top);
  const max = rows[0]?.total_devices ?? 0;
  if (rows.length === 0 || max === 0) {
    return <div className="dash-empty">{t('dashboard.noClientsToDisplay')}</div>;
  }
  const upToDateLabel = t('dashboard.seriesUpToDate');
  const refreshLabel = t('dashboard.seriesRefresh');

  return (
    <div className="dash-bars" data-testid="clients-bar-chart">
      <div className="dash-bars-legend" aria-label="legend">
        <span className="dash-legend-item"><i className="dash-swatch is-uptodate" />{upToDateLabel}</span>
        <span className="dash-legend-item"><i className="dash-swatch is-refresh" />{refreshLabel}</span>
      </div>
      <ol className="dash-bars-list">
        {rows.map((c) => {
          const upToDate = c.total_devices - c.devices_needing_refresh;
          const title = `${c.client_name}\n${upToDateLabel} : ${formatNumber(upToDate)}\n${refreshLabel} : ${formatNumber(c.devices_needing_refresh)}`;
          return (
            <li key={c.client_id} className={`dash-bar-row${onSelect ? ' is-clickable' : ''}${selectedId === c.client_id ? ' is-selected' : ''}`} title={title} data-testid="clients-bar-row"
                onClick={onSelect ? () => onSelect(c) : undefined} role={onSelect ? 'button' : undefined} tabIndex={onSelect ? 0 : undefined}
                onKeyDown={onSelect ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(c); } } : undefined}>
              <span className="dash-bar-name">{c.client_name}{(agencyCount.get(c.client_id) ?? 0) > 0 && <span className="dash-bar-group">{t('clients.agencies.count', { count: agencyCount.get(c.client_id) ?? 0 })}</span>}</span>
              <span className="dash-bar-track" style={{ width: `${(c.total_devices / max) * 100}%` }}>
                {upToDate > 0 && <span className="dash-bar-seg is-uptodate" style={{ flexGrow: upToDate }} />}
                {c.devices_needing_refresh > 0 && <span className="dash-bar-seg is-refresh" style={{ flexGrow: c.devices_needing_refresh }} />}
              </span>
              <span className="dash-bar-value">{formatNumber(c.total_devices)}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
